/**
 * Personalised data (categories, symbols, settings) for one user.
 *
 * - Changes apply immediately on screen and are saved on this device.
 * - They are then sent to the server (debounced). If that fails — offline,
 *   server down — the type stays "dirty" and is retried automatically.
 * - On start, unsynced local changes win over the server copy for that type;
 *   otherwise the server copy (shared across devices) is used.
 *
 * Framework-independent so it can be unit-tested.
 */
import type { DataVersions, UserDataResponse } from '@shared/api';
import {
  DEFAULT_SETTINGS,
  type CustomCategory,
  type CustomSymbol,
  type Hidden,
  type Routine,
  type Settings,
  type Usage,
} from '@shared/schemas';
import { BASE_CATEGORIES } from '@shared/symbols';
import {
  normalizeCategories,
  normalizeHidden,
  normalizeRoutines,
  normalizeSettings,
  normalizeSymbols,
  normalizeUsage,
} from '@shared/userData';

export type DataType = 'categories' | 'symbols' | 'settings' | 'usage' | 'hidden' | 'routines';

export interface BoardData {
  categories: CustomCategory[];
  symbols: CustomSymbol[];
  settings: Settings;
  /** Symbol presses, for the "Frequentes" screen (shared by the account's devices). */
  usage: Usage;
  /** Symbols and categories hidden from the child ("Ocultar"). */
  hidden: Hidden;
  /** Visual routines ("Rotina"). */
  routines: Routine[];
}

export interface BoardState extends BoardData {
  loaded: boolean;
  /**
   * False while neither the server copy nor a local copy is available (first
   * use of this device while offline). Editing is blocked then, otherwise the
   * edit would overwrite the data saved on the server.
   */
  editable: boolean;
  dirty: DataType[];
  syncError: boolean;
}

export interface BoardTransport {
  load(): Promise<UserDataResponse>;
  /** Returns the new server version of that type, when known. */
  save(type: DataType, data: unknown): Promise<string | void>;
  /** Cheap check of what changed on the server; without it, refresh() always loads. */
  versions?(): Promise<DataVersions>;
}

export interface KeyValueStorage {
  readJson<T>(key: string): T | null;
  writeJson(key: string, value: unknown): boolean;
}

interface CachedData extends BoardData {
  dirty: DataType[];
}

const TYPES: DataType[] = ['categories', 'symbols', 'settings', 'usage', 'hidden', 'routines'];

const EMPTY: BoardData = {
  categories: [],
  symbols: [],
  settings: DEFAULT_SETTINGS,
  usage: {},
  hidden: { symbols: [], categories: [] },
  routines: [],
};

/** Fills the legacy `category` name on each symbol (read by the previous app version). */
export function withLegacyCategoryNames(symbols: CustomSymbol[], categories: CustomCategory[]): CustomSymbol[] {
  const names = new Map<string, string>([
    ...BASE_CATEGORIES.map((c) => [c.id, c.name.pt] as [string, string]),
    ...categories.map((c) => [c.id, c.name] as [string, string]),
  ]);
  return symbols.map((s) => {
    const name = names.get(s.categoryId);
    return name && s.category !== name ? { ...s, category: name } : s;
  });
}

export class BoardStore {
  private state: BoardState = { ...EMPTY, loaded: false, editable: false, dirty: [], syncError: false };
  private remoteLoaded = false;
  private listeners = new Set<() => void>();
  private versions = Object.fromEntries(TYPES.map((t) => [t, 0])) as Record<DataType, number>;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private flushing: Promise<void> | null = null;
  private refreshing: Promise<void> | null = null;
  /** Server version of each type as last seen by this device. */
  private known: DataVersions = {};
  private disposed = false;

  constructor(
    private userId: string,
    private transport: BoardTransport,
    private storage: KeyValueStorage,
    private options: { debounceMs?: number; retryMs?: number } = {},
  ) {}

  private get cacheKey() {
    return `data:${this.userId}`;
  }

  getState = (): BoardState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private setState(patch: Partial<BoardState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  private persist() {
    const { categories, symbols, settings, usage, hidden, routines, dirty } = this.state;
    this.storage.writeJson(this.cacheKey, { categories, symbols, settings, usage, hidden, routines, dirty } satisfies CachedData);
  }

  async init(): Promise<void> {
    const cached = this.storage.readJson<CachedData>(this.cacheKey);
    const cachedData = cached ? this.normalize(cached) : null;
    const dirty = cached?.dirty?.filter((t): t is DataType => TYPES.includes(t)) ?? [];
    if (cachedData) this.setState({ ...cachedData, dirty, loaded: true, editable: true });

    try {
      const response = await this.transport.load();
      const remote = this.normalize(response);
      this.known = { ...response.versions };
      const merged = { ...remote };
      // Unsynced changes saved on this device win for their type.
      if (cachedData) for (const type of dirty) (merged as Record<DataType, unknown>)[type] = cachedData[type];
      if (this.disposed) return;
      this.remoteLoaded = true;
      // Local edits made while this load was in flight win.
      const localDirty = this.state.dirty;
      for (const type of localDirty) (merged as unknown as Record<DataType, unknown>)[type] = this.state[type];
      this.setState({ ...merged, dirty: [...new Set([...dirty, ...localDirty])], loaded: true, editable: true, syncError: false });
      this.persist();
      if (this.state.dirty.length) void this.flush();
    } catch {
      if (this.disposed) return;
      if (!cachedData) this.setState({ ...EMPTY, loaded: true, editable: false });
      if (dirty.length) this.scheduleRetry();
    }
  }

  /**
   * Brings in changes saved on another device (tablet at home, phone outside).
   * Pending changes from this device are sent first and always win for their
   * type; types this device did not touch take the server copy. Never throws.
   */
  refresh(): Promise<void> {
    this.refreshing ??= this.doRefresh()
      .catch(() => {
        /* offline or server unreachable: try again on the next trigger */
      })
      .finally(() => {
        this.refreshing = null;
      });
    return this.refreshing;
  }

  private async doRefresh() {
    if (this.disposed) return;
    if (!this.remoteLoaded) return this.init();
    if (this.state.dirty.length) await this.flush();
    if (this.transport.versions) {
      const remoteVersions = await this.transport.versions();
      const changed = TYPES.some((type) => (remoteVersions[type] ?? '') !== (this.known[type] ?? ''));
      if (!changed) return;
    }
    const startVersions = { ...this.versions };
    const dirtyAtStart = [...this.state.dirty];
    const response = await this.transport.load();
    if (this.disposed) return;
    const remote = this.normalize(response);
    const patch: Partial<BoardData> = {};
    for (const type of TYPES) {
      const touchedHere =
        dirtyAtStart.includes(type) || this.state.dirty.includes(type) || this.versions[type] !== startVersions[type];
      if (touchedHere) continue;
      const remoteVersion = response.versions?.[type];
      if (remoteVersion !== undefined) this.known[type] = remoteVersion;
      if (JSON.stringify(remote[type]) !== JSON.stringify(this.state[type])) {
        (patch as Record<DataType, unknown>)[type] = remote[type];
      }
    }
    if (Object.keys(patch).length) {
      this.setState({ ...patch, syncError: false });
      this.persist();
    }
  }

  /** Called when the connection returns: load if we never could, else push pending changes. */
  resync(): Promise<void> {
    return this.remoteLoaded || this.state.editable ? this.flush() : this.init();
  }

  private normalize(data: Partial<BoardData>): BoardData {
    const categories = normalizeCategories(data.categories);
    return {
      categories,
      symbols: normalizeSymbols(data.symbols, categories),
      settings: normalizeSettings(data.settings),
      usage: normalizeUsage(data.usage),
      hidden: normalizeHidden(data.hidden),
      routines: normalizeRoutines(data.routines),
    };
  }

  update<K extends DataType>(type: K, value: BoardData[K]): void;
  update(type: DataType, value: BoardData[DataType]) {
    if (!this.state.editable) return;
    this.versions[type]++;
    const dirty = this.state.dirty.includes(type) ? this.state.dirty : [...this.state.dirty, type];
    this.setState({ [type]: value, dirty } as Partial<BoardState>);
    this.persist();
    this.scheduleFlush();
  }

  /** Several types changed together (e.g. deleting a category and its symbols). */
  updateMany(patch: Partial<BoardData>) {
    for (const [type, value] of Object.entries(patch) as [DataType, BoardData[DataType]][]) this.update(type, value);
  }

  private scheduleFlush() {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => void this.flush(), this.options.debounceMs ?? 600);
  }

  private scheduleRetry() {
    if (this.retryTimer || this.disposed) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, this.options.retryMs ?? 30_000);
  }

  /** Sends every dirty type to the server. Safe to call repeatedly. */
  flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.flushing ??= this.doFlush().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async doFlush() {
    for (const type of [...this.state.dirty]) {
      const version = this.versions[type];
      const payload =
        type === 'symbols' ? withLegacyCategoryNames(this.state.symbols, this.state.categories) : this.state[type];
      try {
        const saved = await this.transport.save(type, payload);
        if (typeof saved === 'string') this.known[type] = saved;
        // Only clear the flag if nothing changed while the request was in flight.
        if (this.versions[type] === version) {
          this.setState({ dirty: this.state.dirty.filter((t) => t !== type), syncError: false });
          this.persist();
        }
      } catch {
        this.setState({ syncError: true });
        this.scheduleRetry();
        return;
      }
    }
    // Changes made during the flush are sent in a follow-up round.
    if (this.state.dirty.length && !this.state.syncError) this.scheduleFlush();
  }

  dispose() {
    this.disposed = true;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.listeners.clear();
  }
}
